import { WixPatternsContainer } from './WixPatternsContainer';
import { Fqdn } from '../model';
import { TaskState } from './TaskState';
import { DataExtensionState } from './DataExtensionState';
import { ExtendedFields, WixPatternsError } from '../services';
import {
  cairoErrorInLoadingAComponent,
  cairoTryAgainClicked,
  loadEnd,
  loadStart,
} from '@wix/bex-utils/@wix/bi-logger-os-data/v2';
import { AppsState } from './AppsState';
import { computed, makeObservable } from 'mobx';

export interface CustomFieldsViewWidgetStateParams {
  readonly container: WixPatternsContainer;
  readonly fqdn: Fqdn;
  readonly url: string;
  readonly route?: string;
  readonly theme?: 'inline' | 'card';
  readonly extensionPoint?: string;
}
export class CustomFieldsViewWidgetState {
  readonly container: WixPatternsContainer;
  readonly dataExtension;
  readonly apps;
  readonly initTask = new TaskState();
  readonly reportBi;
  readonly startTime = performance.now();
  readonly url;
  readonly route;
  readonly theme;
  readonly userFieldsNamespace;

  constructor(params: CustomFieldsViewWidgetStateParams) {
    const { container, fqdn, url, theme, route, extensionPoint } = params;
    this.container = container;
    this.url = url;
    this.route = route;
    this.theme = theme;

    this.reportBi = container.createBILogger({
      componentType: 'CustomFieldsViewWidget',
      ...fqdn,
    });

    this.dataExtension = new DataExtensionState({
      container,
      fqdn,
      extensionPoint,
    });

    this.userFieldsNamespace =
      this.dataExtension.dataExtensionService.userFieldsNamespace;

    this.apps = new AppsState({
      container,
      dataExtension: this.dataExtension,
    });

    this.reportBi(
      loadStart({
        url: this.url,
        theme,
      }),
    );

    makeObservable(this, {
      isLoading: computed,
    });
  }

  init() {
    const { initTask, apps, dataExtension } = this;

    dataExtension.init();
    apps.init();

    initTask.runOnce(async () => {
      dataExtension.initTask.runOnce();
      apps.initTask.runOnce();

      await Promise.all([
        dataExtension.initTask.status.promise?.catch(
          this.onInitError.bind(this),
        ),
        apps.initTask.status.promise?.catch(() => null),
      ]);

      this.reportBi(
        loadEnd({
          url: this.url,
          route: this.route,
          loadingTime: Math.round(performance.now() - this.startTime),
          theme: this.theme,
        }),
      );
    });
  }

  onInitError(err: Error) {
    const cairoError = new WixPatternsError({
      errorCode: 'InitCustomFieldsDataExtensionFailed',
      originalException: err,
    });

    this.container.internalMonitor.reportError({
      error: cairoError,
    });

    this.reportBi(
      cairoErrorInLoadingAComponent({
        errorType: cairoError.name,
      }),
    );

    throw cairoError;
  }

  retry() {
    this.reportBi(
      cairoTryAgainClicked({
        filteredListSize: this.dataExtension.availableUserCustomFields.length,
        actionName: 'Load custom fields',
        loaction: 'View only widget',
      }),
    );

    this.initTask.runOnce();
  }

  get isLoading() {
    const { status } = this.initTask;
    return status.isIdle || status.isLoading;
  }

  get isError() {
    return this.initTask.status.isError;
  }

  getExtendedFieldsToDisplayForNamespace(
    namespace: string,
    extendedFields?: ExtendedFields,
  ) {
    const isUserNamespace = namespace === this.userFieldsNamespace;

    if (isUserNamespace) {
      const userCustomFields = this.dataExtension.availableUserCustomFields;
      const fields =
        extendedFields?.namespaces?.[
          namespace as keyof typeof extendedFields.namespaces
        ] ?? {};
      return userCustomFields.filter((field) => {
        const fieldValue = fields[field.id];
        const isEmpty = fieldValue === null || fieldValue === undefined;
        return !isEmpty;
      });
    }

    const appSchema = this.dataExtension.appSchemas?.find(
      (schema) => schema.namespace === namespace,
    );
    const appFields = appSchema
      ? this.dataExtension.getAvailableCustomFieldsForApp(appSchema)
      : [];

    return appFields;
  }

  getAppName(namespace: string) {
    const appSchema = this.dataExtension.appSchemas?.find(
      (schema) => schema.namespace === namespace,
    );

    return this.apps.appNames[appSchema?.appDefId || ''];
  }
}
